import _ from "lodash";
import React from "react";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import {
  MagicWandThreeStarsOutlined,
  ReloadArrowOutlined,
  FourLinesOutlined,
  TwoLinesOutlined,
  TickLargeOutlined,
  BroomOutlined,
  LanguageOutlined,
  ChevronLeftOutlined,
  AddCircleOutlined,
  DeleteOutlined,
  ReplaceOutlined,
  SetupOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";

export const aiPromptsActionMapping = {
  ADD_BELOW: "add_below",
  DISCARD: "discard",
  REPLACE: "replace",
  KEEP: "keep",
  TRANSLATE: "translate",
  TRANSLATE_BACK: "translate_back",
  DEFAULT_LANGUAGE: "default_language",
};

const DIV_TAG = "<div>";
const NEW_LINE = "\n";

export const aiGenerateActionsMapping = {
  SHORTEN: "shorten",
  ELABORATE: "elaborate",
  FIX_SPELLING_GRAMMAR: "check_for_spelling_and_grammar",
  SIMPLIFY_LANGUAGE: "simplify_writing",
  IMPROVE_LANGUAGE: "improve_writing",
  REGENERATE: "regenerate",
  CHANGE_THIS: "change_this",
  SPARKLE_ICON: "sparkle_icon",
  SPARKLE_CHANGE: "sparkle_change",
  AUTOGENERATE_CAPTION: "autogenerate_caption",
  ASK_AI_TO: "portfolio_help_me_write_with_content",
  CHANGE_THIS_TEXT: "change_this_text",
  AUTOGENERATE_WITH_IMAGE: "autogenerate_with_image",
  ASK_AI_WITHOUT_IMAGE: "ai_caption_image_empty",
  ASK_AI_NOTE_CAPTION: "ai_caption_note_empty",
  ASK_AI_ATTACHMENT_CAPTION: "ai_caption_other_empty",
  ASK_AI_ATTACHMENT_CAPTION_FILLED: "ai_caption_filled",
};

export const LANGUAGE_ACTIONS = AI_TRANSLATION_LANGUAGES;

const DIV_END_TAG = "</div>";

const {
  REGENERATE,
  SHORTEN,
  ELABORATE,
  SIMPLIFY_LANGUAGE,
  FIX_SPELLING_GRAMMAR,
  IMPROVE_LANGUAGE,
  AUTOGENERATE_CAPTION,
} = aiGenerateActionsMapping;

const {
  KEEP,
  DISCARD,
  REPLACE,
  ADD_BELOW,
  TRANSLATE,
  TRANSLATE_BACK,
  DEFAULT_LANGUAGE,
} = aiPromptsActionMapping;

const DIVIDER = "divider";

const COMMON_POST_GENERATION_ACTIONS = [
  SHORTEN,
  ELABORATE,
  IMPROVE_LANGUAGE,
  SIMPLIFY_LANGUAGE,
  FIX_SPELLING_GRAMMAR,
  TRANSLATE,
  DIVIDER,
  DEFAULT_LANGUAGE,
];

const promptsIconMapping = {
  [ADD_BELOW]: <AddCircleOutlined variant="subtle" />,
  [REPLACE]: <ReplaceOutlined variant="subtle" />,
  [KEEP]: <TickLargeOutlined variant="subtle" />,
  [REGENERATE]: <ReloadArrowOutlined variant="subtle" />,
  [DISCARD]: <DeleteOutlined variant="subtle" />,
  [ELABORATE]: <FourLinesOutlined variant="subtle" />,
  [SHORTEN]: <TwoLinesOutlined variant="subtle" />,
  [SIMPLIFY_LANGUAGE]: <BroomOutlined variant="subtle" />,
  [IMPROVE_LANGUAGE]: <PencilLineOutlined variant="subtle" />,
  [FIX_SPELLING_GRAMMAR]: <SetupOutlined variant="subtle" />,
  [TRANSLATE]: <LanguageOutlined variant="subtle" />,
  [TRANSLATE_BACK]: <ChevronLeftOutlined variant="subtle" />,
  [AUTOGENERATE_CAPTION]: <MagicWandThreeStarsOutlined variant="subtle" />,
  [DEFAULT_LANGUAGE]: <LanguageOutlined variant="subtle" />,
};

const promptBoxMenuOptions = (t, defaultLanguagePreference) => [
  {
    key: AUTOGENERATE_CAPTION,
    label: t("journal:autogenerate_caption"),
    icon: promptsIconMapping[AUTOGENERATE_CAPTION],
  },
  {
    key: REPLACE,
    label: t("progressReport:replace_selection"),
    icon: promptsIconMapping[REPLACE],
  },
  {
    key: ADD_BELOW,
    label: t("progressReport:add_below"),
    icon: promptsIconMapping[ADD_BELOW],
  },
  {
    key: KEEP,
    label: t("progressReport:keep"),
    icon: promptsIconMapping[KEEP],
  },
  {
    key: REGENERATE,
    label: t("progressReport:regenerate"),
    icon: promptsIconMapping[REGENERATE],
  },
  {
    key: DISCARD,
    label: t("progressReport:discard"),
    icon: promptsIconMapping[DISCARD],
  },
  {
    key: DIVIDER,
    isDivider: true,
  },
  {
    key: SHORTEN,
    label: t("common:shorten"),
    icon: promptsIconMapping[SHORTEN],
  },
  {
    key: ELABORATE,
    label: t("progressReport:elaborate"),
    icon: promptsIconMapping[ELABORATE],
  },
  {
    key: IMPROVE_LANGUAGE,
    label: t("progressReport:improve_language"),
    icon: promptsIconMapping[IMPROVE_LANGUAGE],
  },
  {
    key: SIMPLIFY_LANGUAGE,
    label: t("progressReport:simplify_language"),
    icon: promptsIconMapping[SIMPLIFY_LANGUAGE],
  },
  {
    key: FIX_SPELLING_GRAMMAR,
    label: t("progressReport:fix_spelling_and_grammer"),
    icon: promptsIconMapping[FIX_SPELLING_GRAMMAR],
  },
  {
    key: TRANSLATE,
    label: t("common:translate_to"),
    icon: promptsIconMapping[TRANSLATE],
    isSubMenu: true,
    options: _.map(LANGUAGE_ACTIONS, language => ({
      key: language.code,
      label: language.label,
    })),
  },
  {
    key: DIVIDER,
    isDivider: true,
  },
  {
    key: DEFAULT_LANGUAGE,
    label: t("common:default_language"),
    icon: promptsIconMapping[DEFAULT_LANGUAGE],
    isSubMenu: true,
    default: _.get(defaultLanguagePreference, "label", null) || null,
    options: _.map(LANGUAGE_ACTIONS, language => ({
      key: `${DEFAULT_LANGUAGE}:${language.id}`,
      label: language.label,
    })),
  },
];

const promptBoxMenuOptionsWithoutAction = t => [
  {
    key: TRANSLATE_BACK,
    label: t("common:translate_to"),
    icon: promptsIconMapping[TRANSLATE_BACK],
  },
];

export const getMenuOptions = ({
  t,
  displayOptions,
  languageOptions = [],
  showAutoGenerateCaption,
  defaultLanguagePreference = {},
}) => {
  let optionsKeys = [];
  let currentOptionsSet = [];
  switch (displayOptions) {
    case "selectedText": {
      optionsKeys = [...COMMON_POST_GENERATION_ACTIONS];
      break;
    }
    case "promptBox": {
      optionsKeys = [
        REPLACE,
        ADD_BELOW,
        REGENERATE,
        DISCARD,
        DIVIDER,
        ...COMMON_POST_GENERATION_ACTIONS,
      ];
      break;
    }
    case "noSelectionWand": {
      optionsKeys = [
        KEEP,
        REGENERATE,
        DISCARD,
        DIVIDER,
        ...COMMON_POST_GENERATION_ACTIONS,
      ];
      break;
    }
    case "selectedTextDisabled":
      optionsKeys = [];
      if (showAutoGenerateCaption) {
        optionsKeys.unshift(AUTOGENERATE_CAPTION);
      }
      break;
    case "translate": {
      const searchOptions = [
        ...promptBoxMenuOptionsWithoutAction(t),
        { key: "search", label: "" },
        ...languageOptions,
      ];
      return searchOptions;
    }
    default:
      return [];
  }
  currentOptionsSet = _.filter(
    promptBoxMenuOptions(t, defaultLanguagePreference),
    option => optionsKeys.includes(option.key)
  );
  return currentOptionsSet;
};

export const getOffsetWithinElement = ({ element, offset, textInputRef }) => {
  let currentNode = element;
  let currentOffset = offset;

  while (currentNode && textInputRef && currentNode !== textInputRef.domElm) {
    if (currentNode.previousSibling) {
      currentNode = currentNode.previousSibling;
      currentOffset += currentNode.childNodes.length;
    } else {
      currentNode = currentNode.parentNode;
    }
  }

  return currentOffset;
};

const getTextNodes = node => {
  const textNodes = [];

  const walk = document.createTreeWalker(
    node,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let currentNode = walk.nextNode();

  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walk.nextNode();
  }

  return textNodes;
};

const findTextNode = (textNodes, searchText) => {
  for (let i = 0; i < textNodes.length; i++) {
    if (textNodes[i].textContent.includes(searchText)) {
      return textNodes[i];
    }
  }

  return null;
};

export const highlightText = ({ ref, text }) => {
  if (ref) {
    const range = document.createRange();
    const textNodes = getTextNodes(ref);
    const node = findTextNode(textNodes, text);
    if (node) {
      const startOffset = node.textContent.indexOf(text);
      const endOffset = startOffset + text.length;
      range.setStart(node, startOffset);
      range.setEnd(node, endOffset);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
};

export const isEmptyHtmlV2 = (htmlString = "") => {
  return _.isEmpty(
    _.trim(
      htmlString
        .replace(/\s+(?=[^<>]*<)/g, "")
        .replace(/&nbsp;/gi, "")
        .replace(/<div>/gi, "")
        .replace(/<div dir="rtl">/gi, "")
        .replace(/<br>/gi, "")
        .replace(/<\/div>/gi, "")
    )
  );
};
export const cleanupMessage = ({ message }) => {
  return message === "<br>" || message === "<div><br></div>" ? "" : message;
};

export const getRangeDiv = range => {
  const cursorPositionDiv = document.createElement("div");
  cursorPositionDiv.appendChild(range.cloneContents());
  return cursorPositionDiv;
};

export const getCaretPositionIncludingHTML = element => {
  const sel = window.getSelection();

  if (sel.rangeCount === 0) return 0;

  const range = sel.getRangeAt(0);
  const clonedRange = range.cloneRange();
  if (element instanceof Node) {
    clonedRange.selectNodeContents(element);
  }
  clonedRange.setEnd(range.endContainer, range.endOffset);

  // Get the cursor position within the element excluding HTML tags for setter method
  const cursorPosition = clonedRange.toString().length;

  // getting cursor-position and selection content from range
  const positionRangeDiv = getRangeDiv(clonedRange);
  const selectionRangeDiv = getRangeDiv(range);

  let cursorPositionIncludingHTML = positionRangeDiv.innerHTML.length;

  // Check if the last characters are </div>
  if (_.endsWith(positionRangeDiv.innerHTML, DIV_END_TAG)) {
    // Subtract the length of </div> from cursorPositionIncludingHTML
    cursorPositionIncludingHTML -= DIV_END_TAG.length;
  }

  // Return length of the reconstructed HTML up to the caret
  return {
    cursorPositionIncludingHTML,
    selectedText: _.replace(selectionRangeDiv.innerHTML, /<\/div>$/, ""),
    cursorPosition,
  };
};

export const setPositionInRange = ({ element, cursorPosition, range }) => {
  if (!element) return cursorPosition;

  // for text nodes
  if (element.nodeType === Node.TEXT_NODE) {
    if (element.textContent.length < cursorPosition) {
      cursorPosition -= element.textContent.length;
    } else {
      range.setEnd(element, cursorPosition);
      cursorPosition = 0;
    }
  } else {
    //div, span, nodes
    for (
      let childIndex = 0;
      childIndex < element.childNodes.length;
      childIndex++
    ) {
      cursorPosition = setPositionInRange({
        element: element.childNodes[childIndex],
        cursorPosition,
        range,
      });
      if (cursorPosition === 0) {
        break;
      }
    }
  }
  return cursorPosition;
};

// To set the caret position in a given element at a specific character count.
export const setCaretPositionIncludingHTML = (element, cursorPosition) => {
  const range = document.createRange();
  const sel = window.getSelection();

  setPositionInRange({
    element,
    cursorPosition,
    range,
  });

  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const handleHighlightedTextRemoval = ({ message }) =>
  _.replace(
    message,
    /<span style="background-color: rgba\(106, 177, 255, 0.4\);[\s]*padding-top: 3px;">([\s\S]*?)<\/span>/g,
    "$1"
  );

export const getNextBlockIndex = ({
  message = "",
  cursorPositionIncludingHTML,
}) => {
  let preCursorText = message?.slice(0, cursorPositionIncludingHTML);
  const postCursorText = message?.slice(cursorPositionIncludingHTML);

  // If the last character of the preCursorText is a '/', remove it.
  if (_.endsWith(preCursorText, "/")) {
    preCursorText = _.trimEnd(preCursorText, "/");
  }

  // case where '/' is added in new line
  if (_.endsWith(preCursorText, "<div>/</div>")) {
    preCursorText = _.trimEnd(preCursorText, "<div>/</div>");
  }

  // any new line detection
  let divIndex = postCursorText.indexOf(DIV_TAG);
  let lineBreakIndex = postCursorText.indexOf(NEW_LINE);

  divIndex = divIndex === -1 ? Infinity : divIndex;
  lineBreakIndex = lineBreakIndex === -1 ? Infinity : lineBreakIndex;

  // finding index of first occurence of new line or div
  const placementIndex = _.min([divIndex, lineBreakIndex]);

  return { preCursorText, postCursorText, placementIndex };
};

export const insertNextBlockHandler = ({
  preCursorText,
  postCursorText,
  placementIndex,
  contentInserted,
  isMessageEmpty = false,
}) => {
  if (isMessageEmpty) {
    return preCursorText + postCursorText + contentInserted;
  }

  if (placementIndex !== Infinity) {
    const preInsertionText = postCursorText.slice(0, placementIndex);
    const postInsertionText = postCursorText.slice(placementIndex);
    return (
      preCursorText +
      preInsertionText +
      NEW_LINE +
      contentInserted +
      postInsertionText
    );
  } else {
    return preCursorText + postCursorText + NEW_LINE + contentInserted;
  }
};

export const cleanSelectedText = ({ message = "" }) =>
  message
    .replace(/<div>/gi, "")
    .replace(/<div dir="rtl">/gi, "")
    .replace(/<br>/gi, NEW_LINE)
    .replace(/<\/div>/gi, "");

export const replaceDivWithNewLine = ({ message = "" }) =>
  message
    .replace(/(.)<div>/g, "$1\n")
    .replace(/<div>/gi, "")
    .replace(/<div dir="rtl">/gi, "")
    .replace(/<br>/gi, NEW_LINE)
    .replace(/<\/div>/gi, "");
